import React from "react";

import { AaaShippingType } from "../../../../models/aaaGameConceptModels";
import { AaaGameInformationDistributionInformationDataType } from "../../../../models/forms";
import { ReadOnlyFormItem } from "../appInfoModelsAndHelpers";
import { ReadOnlyForm } from "../ReadOnlyForm";

type ReadOnlyAaaDistributionInformationProps = {
  dataInForm: AaaGameInformationDistributionInformationDataType;
};

const mapTerritoryNameToTranslatedStringKey = (input: string): string => {
  const mappings: { [key: string]: string } = {
    asia: "asianTerritory",
    australia: "australianTerritory",
    europe: "europeanTerritory",
    japan: "japanTerritory",
    northAmerica: "northAmericanTerritory",
    southAmerica: "southAmericanTerritory",
  };

  return mappings[input] || "";
};

const AaaDistributionInformationConfig: Map<string, ReadOnlyFormItem> = new Map([
  [
    "shippingStrategy",
    {
      title: "aaaDistInfoShippingTitle",
      mapper: (
        data: AaaGameInformationDistributionInformationDataType,
        localizedStrings: Map<string, string>
      ) =>
        data.shippingStrategy === AaaShippingType.retailAndDigital
          ? localizedStrings.get("retailAndDigital")
          : data.shippingStrategy === AaaShippingType.digitalOnlyF2P
          ? localizedStrings.get("digitalOnlyAndF2P")
          : data.shippingStrategy === AaaShippingType.digitalOnly
          ? localizedStrings.get("digitalOnly")
          : null,
    },
  ],
  [
    "howItWillGenerateMoney",
    {
      title: "indicateHowGameWillGenerateRevenue",
    },
  ],
  [
    "retentionMechanics",
    {
      title: "whatRetentionMechanicsAreInGame",
    },
  ],
  [
    "inGamePricePoints",
    {
      title: "describeYourInGamePricePoints",
    },
  ],
  [
    "whatPlayersCanSpendOn",
    {
      title: "describeWhatPlayersCanSpendOn",
    },
  ],
  [
    "timeUntilAPlayerIsPromptedToPay",
    {
      title: "howLongUntilANewPlayerWillPay",
    },
  ],
  [
    "howItemsCanBeGifted",
    {
      title: "describeHowItemsCanBeGifted",
    },
  ],
  [
    "subscriptionModel",
    {
      title: "describeYourSubscriptionModel",
    },
  ],
  [
    "prevReleases",
    {
      title: "aaaDistInfoListReleases",
      mapper: (data: AaaGameInformationDistributionInformationDataType) =>
        data.isPrevIndependentlyPublished ? data.prevReleases : null,
    },
  ],
  [
    "primaryPublisherName",
    {
      mapper: (data: AaaGameInformationDistributionInformationDataType) =>
        data.isAssumingResponsibilitesForPrevPublishedGame ? data.primaryPublisherName : null,
    },
  ],
  [
    "certResponsiblePartner",
    {
      title: "partnerResponsibleForCertification",
      mapper: (data: AaaGameInformationDistributionInformationDataType) =>
        data.isResponsibleForCertification ? data.certResponsiblePartner : null,
    },
  ],
  [
    "publishingTerritories",
    {
      title: "aaaDistInfoPublishingRightsTitle",
      mapper: (
        data: AaaGameInformationDistributionInformationDataType,
        localizedStrings: Map<string, string>
      ) => {
        const primaryPublisher = localizedStrings.get("primaryPublisher");
        const subPublisher = localizedStrings.get("subPublisher");
        return Object.keys(data.publishingTerritories).map(territoryName => {
          const primaryPublisherName = data.publishingTerritories[territoryName].publisherName;
          const subPublisherName = data.publishingTerritories[territoryName].subPublisherName;
          return `${localizedStrings.get(mapTerritoryNameToTranslatedStringKey(territoryName))}${
            primaryPublisherName ? ` - ${primaryPublisher}: ${primaryPublisherName}` : ""
          }${subPublisherName ? ` - ${subPublisher}: ${subPublisherName}` : ""}`;
        });
      },
    },
  ],
]);

export const ReadOnlyAaaDistributionInformation = ({
  dataInForm,
}: ReadOnlyAaaDistributionInformationProps) => {
  const extraStringsToLocalize = [
    "retailAndDigital",
    "digitalOnlyAndF2P",
    "digitalOnly",
    "indicateHowGameWillGenerateRevenue",
    "whatRetentionMechanicsAreInGame",
    "describeYourInGamePricePoints",
    "describeWhatPlayersCanSpendOn",
    "howLongUntilANewPlayerWillPay",
    "describeHowItemsCanBeGifted",
    "describeYourSubscriptionModel",
    "asianTerritory",
    "australianTerritory",
    "europeanTerritory",
    "japanTerritory",
    "northAmericanTerritory",
    "southAmericanTerritory",
    "primaryPublisher",
    "subPublisher",
  ];
  return (
    <ReadOnlyForm
      formConfig={AaaDistributionInformationConfig}
      formData={dataInForm}
      extraStringsToLocalize={extraStringsToLocalize}
    />
  );
};
